<template>
  <div class="page">
    <div class="cashup-card" v-if="currentUser">
      <div class="card-body">
        <div class="card-header-content">
          <h1 class="card-title">
            <span class="title-accent">Cash</span> Up
          </h1>
          <div class="date-badge">{{ new Date().toLocaleDateString() }}</div>
        </div>

        <form @submit.prevent="confirmationText">
          <!-- Cashier Selection Section -->
          <div class="form-section">
            <div class="section-header">
              <i class="fas fa-user-circle"></i> Cashier Information
            </div>
            <div class="section-content">
              <div class="form-group" v-if="currentUser.Type.toLowerCase() !== 'admin'">
                <label for="floatingTextInput1">Cashier</label>
                <input type="text" class="form-control" v-model="cashup_user" id="floatingTextInput1"
                  placeholder="Cashier" required readonly>
              </div>

              <div v-else class="form-group">
                <label for="current_cashup_user">Cashier</label>
                <select v-model="cashup_user" class="form-select" id="current_cashup_user">
                  <option v-for="(user, n) in users" :key="n + 1" :value="user.Username">
                    {{ user.Username }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Cash Denominations Section -->
          <div class="form-section">
            <div class="section-header">
              <div class="header-with-total">
                <div class="header-title">
                  <i class="fas fa-money-bill-wave"></i> Cash Denominations
                </div>
                <div class="total-badge">
                  Total: {{ $utils.formatMoney(totalCashVal) }}
                </div>
              </div>
            </div>

            <div class="section-content">
              <!-- Higher Denominations -->
              <div class="denomination-group">
                <div class="denomination-group-title">High Value Notes</div>
                <div class="denomination-grid">
                  <div class="denomination-card high">
                    <div class="card-header">
                      <h5>K500 Notes</h5>
                      <span class="value-badge primary">{{ $utils.formatMoney(five_hundredsVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.five_hundreds"
                      id="five_hundreds" placeholder="0">
                  </div>

                  <div class="denomination-card high">
                    <div class="card-header">
                      <h5>K200 Notes</h5>
                      <span class="value-badge primary">{{ $utils.formatMoney(two_hundredsVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.two_hundreds"
                      id="two_hundreds" placeholder="0">
                  </div>

                  <div class="denomination-card high">
                    <div class="card-header">
                      <h5>K100 Notes</h5>
                      <span class="value-badge primary">{{ $utils.formatMoney(hundredsVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.hundreds"
                      id="hundreds" placeholder="0">
                  </div>
                </div>
              </div>

              <!-- Medium Denominations -->
              <div class="denomination-group">
                <div class="denomination-group-title">Medium Value Notes</div>
                <div class="denomination-grid">
                  <div class="denomination-card medium">
                    <div class="card-header">
                      <h5>K50 Notes</h5>
                      <span class="value-badge info">{{ $utils.formatMoney(fiftiesVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.fifties"
                      id="fifties" placeholder="0">
                  </div>

                  <div class="denomination-card medium">
                    <div class="card-header">
                      <h5>K20 Notes</h5>
                      <span class="value-badge info">{{ $utils.formatMoney(twentiesVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.twenties"
                      id="twenties" placeholder="0">
                  </div>

                  <div class="denomination-card medium">
                    <div class="card-header">
                      <h5>K10 Notes</h5>
                      <span class="value-badge info">{{ $utils.formatMoney(tensVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.tens" 
                      id="tens" placeholder="0">
                  </div>
                </div>
              </div>

              <!-- Low Denominations -->
              <div class="denomination-group">
                <div class="denomination-group-title">Coins</div>
                <div class="denomination-grid">
                  <div class="denomination-card low">
                    <div class="card-header">
                      <h5>K5 Coins</h5>
                      <span class="value-badge secondary">{{ $utils.formatMoney(fivesVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.fives" 
                      id="fives" placeholder="0">
                  </div>

                  <div class="denomination-card low">
                    <div class="card-header">
                      <h5>K2 Coins</h5>
                      <span class="value-badge secondary">{{ $utils.formatMoney(twosVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.twos" 
                      id="twos" placeholder="0">
                  </div>

                  <div class="denomination-card low">
                    <div class="card-header">
                      <h5>K1 Coins</h5>
                      <span class="value-badge secondary">{{ $utils.formatMoney(onesVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.ones" 
                      id="ones" placeholder="0">
                  </div>
                </div>
              </div>

              <!-- Small Coins -->
              <div class="denomination-group">
                <div class="denomination-group-title">Small Coins</div>
                <div class="denomination-grid two-columns">
                  <div class="denomination-card coins">
                    <div class="card-header">
                      <h5>50 Ngwee Coins</h5>
                      <span class="value-badge dark">{{ $utils.formatMoney(fifty_ngweesVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.fifty_ngwees"
                      id="fifty_ngwees" placeholder="0">
                  </div>

                  <div class="denomination-card coins">
                    <div class="card-header">
                      <h5>10 Ngwee Coins</h5>
                      <span class="value-badge dark">{{ $utils.formatMoney(ten_ngweesVal) }}</span>
                    </div>
                    <input type="number" min="0" class="form-control" v-model="cash.ten_ngwees"
                      id="ten_ngwees" placeholder="0">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Section -->
          <div class="form-section">
            <div class="section-header">
              <div class="header-title">
                <i class="fas fa-calculator"></i> Payment Totals
              </div>
            </div>
            <div class="section-content">
              <!-- Cash Total -->
              <div class="summary-item cash-total">
                <div class="summary-header">
                  <h5><i class="fas fa-money-bill-alt"></i> Cash Total</h5>
                  <h5 class="total-value success">{{ $utils.formatMoney(totalCashVal) }}</h5>
                </div>
                <div class="progress-bar">
                  <div class="progress-fill success"></div>
                </div>
              </div>

              <!-- Payment Methods Grid -->
              <div class="payment-methods-grid">
                <!-- Card -->
                <div class="payment-method-item">
                  <div class="method-header">
                    <h5><i class="fas fa-credit-card"></i> Card Total</h5>
                  </div>
                  <input type="number" min="0" step="0.01" class="form-control" v-model="card" id="card-total" placeholder="Card total">
                </div>

                <!-- Other Payment Methods -->
                <div v-for="method in otherMethods" :key="method.id" class="payment-method-item">
                  <div class="method-header">
                    <h5><i class="fas fa-wallet"></i> {{ method.name }} Total</h5>
                  </div>
                  <input type="number" min="0" step="0.01" class="form-control" v-model="method.amount" :id="method.id + `-total`" :placeholder="`${method.name} total`">
                </div>
              </div>

              <!-- Grand Total -->
              <div class="grand-total">
                <div class="total-header">
                  <h5><i class="fas fa-coins"></i> Grand Total</h5>
                  <h5 class="total-value">{{ $utils.formatMoney(grandTotal) }}</h5>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="submit-container">
            <button :disabled="loading" type="submit" class="submit-button">
              <span v-if="loading" class="spinner"></span>
              <i class="fas fa-check-circle"></i> Submit Cash Up ({{ $utils.formatMoney(grandTotal) }})
            </button>
          </div>
        </form>
      </div>
    </div>
    <FinalizeCashupSheet :cashup="cashup" ref="detailsheet" />
  </div>
</template>

<script>
import FinalizeCashupSheet from "@/components/point-of-sale/cashup/FinalizeCashupSheet";
import { Storage } from "@/services/Storage";

export default {
  name: "BasicCashupForm",
  components: { FinalizeCashupSheet },
  async beforeMount() {
    let user = await Storage.getCurrentUser();
    this.currentUser = user;

    this.users = await Storage.getAllUsers();
    this.cashup_user = user.Username;

    // Offline backlog (POST/GET deviceOfflineStatus) is owned by CashupView — not this form.

    // Check if user has already cashed up today
    const hasCashedUpToday = await this.$store.dispatch('checkUserCashedUpToday', user.Username);
    if (hasCashedUpToday) {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Already Cashed Up',
            text: `You have already cashed up today. Please note that cashing up multiple times in one day may cause discrepancies in your reports.`,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Continue Anyway',
            cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) {
            this.$router.push({ name: 'float' });
            return;
        }
    }

    const hasPermission = await this.$store.dispatch('validateSecurityPermissionNoPrompt', 'cashupWithOpenTables');

    if (!hasPermission) {
        const { hasOpenTables, count } = await this.$store.dispatch('checkForOpenTables')
            .catch(error => {
                console.error('Error checking open tables:', error);
                return { hasOpenTables: false, count: 0 };
            });

        if (hasOpenTables) {
            await Swal.fire({
                icon: 'warning',
                title: 'Cannot Cash Up',
                text: `You have ${count} open table(s). Please close all tables before cashing up.`
            });
            this.$router.push({ name: 'tables' });
            return;
        }
    }
  },
  computed: {
    set() {
      return this.$store.getters.getDeviceSettings
    },
    five_hundredsVal() {
      return Number(Number(this.cash.five_hundreds) * 500)
    },
    two_hundredsVal() {
      return Number(Number(this.cash.two_hundreds) * 200)
    },
    hundredsVal() {
      return Number(Number(this.cash.hundreds) * 100)
    },
    fiftiesVal() {
      return Number(Number(this.cash.fifties) * 50)
    },
    twentiesVal() {
      return Number(Number(this.cash.twenties) * 20)
    },
    tensVal() {
      return Number(Number(this.cash.tens) * 10)
    },
    fivesVal() {
      return Number(Number(this.cash.fives) * 5)
    },
    twosVal() {
      return Number(Number(this.cash.twos) * 2)
    },
    onesVal() {
      return Number(this.cash.ones)
    },
    fifty_ngweesVal() {
      return Number(Number(this.cash.fifty_ngwees) * 0.5)
    },
    ten_ngweesVal() {
      return Number(Number(this.cash.ten_ngwees) * 0.1)
    },
    otherMethods() {
      if (this.otherPayments.length === 0) {
        this.otherPayments = this.$store.getters.getPaymentMethods
          .filter(method => method.active)
          .map(method => ({
            ...method,
            amount: 0
          }));
      }
      return this.otherPayments;
    },
    totalCashVal() {
      return this.five_hundredsVal
        + this.two_hundredsVal
        + this.hundredsVal
        + this.fiftiesVal
        + this.twentiesVal
        + this.tensVal
        + this.fivesVal
        + this.twosVal
        + this.onesVal
        + this.fifty_ngweesVal
        + this.ten_ngweesVal
    },
    grandTotal() {


      let otherTotal = this.otherMethods.reduce((sum, method) => {
        let amount = Number(method.amount) || 0;
        return sum + amount;
      }, 0);

      return this.totalCashVal + Number(this.card) + otherTotal;
    },
    loading() {
      return this.$store.getters.getLoadingState;
    },
  },
  data() {
    return {
      cashup: {},
      pc_cashup: false,
      cashup_user: '',
      currentUser: null,
      users: [],
      cash: {
        five_hundreds: 0,
        two_hundreds: 0,
        hundreds: 0,
        fifties: 0,
        twenties: 0,
        tens: 0,
        fives: 0,
        twos: 0,
        ones: 0,
        fifty_ngwees: 0,
        ten_ngwees: 0,
      },
      card: 0,
      otherPayments: []
    }
  },

  methods: {
    async confirmationText() {
      try {

        const hasPermission = await this.$store.dispatch('validateSecurityPermissionNoPrompt', 'cashupWithOpenTables');


        if (!hasPermission) {
            const { hasOpenTables, count } = await this.$store.dispatch('checkForOpenTables');
            if (hasOpenTables) {
                return Swal.fire({
                    icon: 'warning',
                    title: 'Cannot Cash Up',
                    text: `You have ${count} open table(s). Please close all tables before cashing up.`
                });
            }
        }


        return Swal.fire({
          title: 'Are you sure?',
          text: "Please make sure your figures are correct!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, I am sure!'
        }).then((result) => {
          if (result.isConfirmed) {
            return this.submitCashUp();
          }
        });
      } catch (error) {
        console.error('Error:', error);
      }
    },
    async submitCashUp() {
      let other = this.otherMethods.map(method => {
        return {
          id: method.id,
          amount: Number(method.amount)
        }
      });

      // Calculate total of other payments
      let otherTotal = other.reduce((sum, method) => sum + method.amount, 0);

      let settings = await Storage.getDeviceSettings();
      let data = {
        cash: parseFloat(this.totalCashVal),
        card: parseFloat(this.card),
        other: other,
        otherTotal: otherTotal,
        pc: settings.PC,
        user: this.cashup_user,
      }
      let isAllowed = await this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `viewCashupDetails`);
      // if (!isAllowed) {
      //   return this.$store.dispatch('cashUpUser', data)
      //     .then(() => this.$router.push({ name: 'float' }))
      //     .finally(() => this.$utils.hideLoading())
      // }
      this.$utils.showLoading()
      return this.$store.dispatch('getcashUpDiff', data)
        .then((res) => {
          this.cashup = res.response;
          this.openSheet();
        }).finally(() => this.$utils.hideLoading());
    },
    openSheet() {
      this.$refs.detailsheet.open();
    },
  }

}
</script>

<style lang="scss" scoped>
.page {
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 24px;
  background: var(--bg-gradient);
  min-height: 100vh;
  font-family: var(--font-family);
}

// Main card styling
.cashup-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  border: var(--card-border);
  margin: 16px 16px;
  width: calc(100% - 32px);
  transition: var(--transition-speed);
  animation: fadeIn 0.5s ease;
  overflow: hidden;

  &:hover {
    box-shadow: var(--card-shadow-hover);
  }

  .card-body {
    padding: 24px;
  }

  .card-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: var(--card-border);

    .card-title {
      font-size: 1.8rem;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      display: flex;
      align-items: center;
      gap: 8px;

      .title-accent {
        color: var(--accent-electric-blue);
      }
    }

    .date-badge {
      background: var(--accent-electric-blue);
      color: white;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.9rem;
      box-shadow: var(--card-shadow);
    }
  }
}

// Form section styling
.form-section {
  margin-bottom: 24px;
  animation: fadeIn 0.3s ease;

  .section-header {
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--text-primary);
    padding-bottom: 8px;
    margin-bottom: 16px;
    border-bottom: var(--card-border);
    display: flex;
    align-items: center;

    .header-with-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .header-title {
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: var(--accent-electric-blue);
        }
      }

      .total-badge {
        background: var(--success);
        color: white;
        padding: 6px 12px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 0.9rem;
      }
    }

    i {
      margin-right: 8px;
      color: var(--accent-electric-blue);
    }
  }

  .section-content {
    background: var(--bg-primary);
    border-radius: 8px;
    padding: 16px;
    box-shadow: var(--card-shadow);
  }

  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      font-weight: 500;
      margin-bottom: 8px;
      color: var(--text-primary);
    }

    .form-control, .form-select {
      width: 100%;
      padding: 10px 12px;
      border: var(--card-border);
      border-radius: 8px;
      background-color: var(--card-bg);
      color: var(--text-primary);
      transition: var(--transition-speed);
      font-size: 0.95rem;

      &:focus {
        border-color: var(--accent-electric-blue);
        box-shadow: 0 0 0 0.2rem rgba(51, 156, 255, 0.25);
        outline: none;
      }
    }
  }
}

// Denomination styling
.denomination-group {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  .denomination-group-title {
    font-weight: 600;
    font-size: 1rem;
    color: var(--text-secondary);
    margin-bottom: 12px;
    padding-left: 8px;
    border-left: 3px solid var(--accent-electric-blue);
  }

  .denomination-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    &.two-columns {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.denomination-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--card-shadow);
  transition: var(--transition-speed);
  border: var(--card-border);

  &:hover {
    box-shadow: var(--card-shadow-hover);
    transform: translateY(-2px);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h5 {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0;
    }

    .value-badge {
      padding: 4px 10px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 0.9rem;
      color: white;

      &.primary {
        background: var(--accent-electric-blue);
      }

      &.info {
        background: var(--accent-neon-purple);
      }

      &.secondary {
        background: var(--text-secondary);
      }

      &.dark {
        background: var(--text-primary);
      }
    }
  }

  .form-control {
    width: 100%;
    padding: 10px 12px;
    border: var(--card-border);
    border-radius: 8px;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    transition: var(--transition-speed);
    font-size: 0.95rem;

    &:focus {
      border-color: var(--accent-electric-blue);
      box-shadow: 0 0 0 0.2rem rgba(51, 156, 255, 0.25);
      outline: none;
    }
  }

  &.high {
    border-left: 4px solid var(--accent-electric-blue);
  }

  &.medium {
    border-left: 4px solid var(--accent-neon-purple);
  }

  &.low {
    border-left: 4px solid var(--text-secondary);
  }

  &.coins {
    border-left: 4px solid var(--text-primary);
  }
}

// Payment methods styling
.summary-item {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: var(--card-shadow);
  transition: var(--transition-speed);

  &:hover {
    box-shadow: var(--card-shadow-hover);
  }

  &.cash-total {
    background: linear-gradient(to right, rgba(var(--success-rgb), 0.1), rgba(var(--success-rgb), 0.05));
    border-left: 4px solid var(--success);
  }

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    h5 {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        color: var(--success);
      }
    }

    .total-value {
      font-weight: 700;

      &.success {
        color: var(--success);
      }
    }
  }

  .progress-bar {
    height: 4px;
    background: rgba(var(--success-rgb), 0.2);
    border-radius: 2px;
    overflow: hidden;

    .progress-fill {
      height: 100%;
      width: 100%;
      background: var(--success);
    }
  }
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.payment-method-item {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--card-shadow);
  transition: var(--transition-speed);
  border: var(--card-border);

  &:hover {
    box-shadow: var(--card-shadow-hover);
    transform: translateY(-2px);
  }

  .method-header {
    margin-bottom: 12px;

    h5 {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        color: var(--accent-electric-blue);
      }
    }
  }

  .form-control {
    width: 100%;
    padding: 10px 12px;
    border: var(--card-border);
    border-radius: 8px;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    transition: var(--transition-speed);
    font-size: 0.95rem;

    &:focus {
      border-color: var(--accent-electric-blue);
      box-shadow: 0 0 0 0.2rem rgba(51, 156, 255, 0.25);
      outline: none;
    }
  }
}

.grand-total {
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--card-shadow-hover);

  .total-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h5 {
      font-size: 1.1rem;
      font-weight: 700;
      color: white;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .total-value {
      font-size: 1.2rem;
    }
  }
}

// Submit button styling
.submit-container {
  text-align: center;
  margin-top: 24px;
}

.submit-button {
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 32px;
  min-width: 250px;
  height: 50px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: none;
  transition: var(--transition-speed);
  box-shadow: var(--card-shadow);
  font-size: 1.1rem;

  &:hover:not(:disabled) {
    box-shadow: var(--card-shadow-hover);
    transform: translateY(-2px);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }

  .spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s ease-in-out infinite;
  }
}

// Remove spinner from number inputs
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .denomination-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .payment-methods-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .denomination-grid, 
  .denomination-grid.two-columns,
  .payment-methods-grid {
    grid-template-columns: 1fr !important;
  }

  .card-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .date-badge {
    align-self: flex-end;
  }
}
</style>
